import React from 'react';
import {useTranslation} from 'react-i18next';
import {useMatch} from 'react-router';

import {ActionButton, Button, Collapse, EButtonAppearance, showModal} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import EditIcon from '@edna/icons/edit.svg';
import KeyboardArrowDownIcon from '@edna/icons/keyboardArrowDown.svg';

import {Badge} from 'src/components';
import {InlineLinkButton, NameLink, Text} from 'src/components/primitives';
import {EChannelType, TItemId, TSubject, channelIconHash} from 'src/constants';
import {CancelLicenseModal} from 'src/containers/Licenses/CancelLicenseModal';
import {ELicenseStatus, TLicense, TWhatsAppLicense} from 'src/containers/Licenses/definitions';
import {colorHash, isActiveLicense} from 'src/containers/Licenses/utils';
import {useGetApprovedSubjectsQuery} from 'src/containers/Subjects/api';
import {TAvailableTariffResponse} from 'src/containers/Tariffs';
import {EPermission, PermissionGuard} from 'src/containers/User';
import {BlockIcon, ReturnIcon} from 'src/icons';
import PATHS from 'src/paths';
import {parseISO} from 'src/utils';

import {ChangeModal, ConfirmCancelModal, showTariffModal} from './Modals';
import {LICENSE_CHANNELS, TSubjectLicense} from './definitions';
import * as S from './style';

const dateFormat = 'dd.MM.yyyy';

export const LicenseDate = React.memo<{item: TLicense | TSubjectLicense; className?: string}>(
  ({item, className}) => {
    const {t} = useTranslation();

    if (item.scheduledCancelAt) {
      return (
        <S.Td isSmallText className={className}>
          <S.Text>{t('Licenses:scheduledCancel')}</S.Text>
          {parseISO(item.scheduledCancelAt, dateFormat)}
        </S.Td>
      );
    }

    if (
      [EChannelType.OK_NOTIFY, EChannelType.VK_NOTIFY, EChannelType.PUSH].includes(item.channelType)
    ) {
      return (
        <S.Td isSmallText className={className}>
          <S.Text>{t('Licenses:accordingToTerms')}</S.Text>
        </S.Td>
      );
    }

    switch (item.status) {
      case ELicenseStatus.EXPIRED: {
        return (
          <S.Td isSmallText className={className}>
            <S.Text>{t('Licenses:expiredAt')}</S.Text>
            {parseISO(item.expiredAt, dateFormat)}
          </S.Td>
        );
      }

      case ELicenseStatus.NEW: {
        return (
          <S.Td isSmallText className={className}>
            <S.Text>{t('Licenses:createdAt')}</S.Text>
            {parseISO(item.createdAt, dateFormat)}
          </S.Td>
        );
      }

      case ELicenseStatus.ACTIVE:

      case ELicenseStatus.ACTIVE_DEBT: {
        const title = t('Licenses:validUntilHint', {
          paidUntil: item.paidUntil ? parseISO(item.paidUntil, dateFormat) : '-',
          activatedAt: item.activatedAt ? parseISO(item.activatedAt, dateFormat) : '-',
        });

        return (
          <S.Td title={title} isSmallText className={className}>
            <S.Text>{t('Licenses:validUntil')}</S.Text>
            {item.activeUntil ? parseISO(item.activeUntil, dateFormat) : '-'}
          </S.Td>
        );
      }

      case ELicenseStatus.CANCELLED: {
        return (
          <S.Td isSmallText className={className}>
            <S.Text>{t('Licenses:cancelledAt')}</S.Text>
            {item.cancelledAt ? parseISO(item.cancelledAt, dateFormat) : '-'}
          </S.Td>
        );
      }

      case ELicenseStatus.TEMPORARY_BLOCKED: {
        return (
          <S.Td isSmallText className={className}>
            <S.Text>{t('Licenses:temporaryblockedAt')}</S.Text>
            {item.temporaryblockedAt ? parseISO(item.temporaryblockedAt, dateFormat) : '-'}
          </S.Td>
        );
      }
    }

    return null;
  },
);

const Actions = React.memo<{item: TLicense | TSubjectLicense}>(({item}) => {
  const {t} = useTranslation();
  const options = React.useMemo(
    () => [
      {
        content: (
          <Stack direction="ROW" align="center" size="2">
            <Icon size="16px" as={EditIcon} />
            <span>{t('Licenses:options.edit')}</span>
          </Stack>
        ),
        handler: () =>
          showModal((closeModal) =>
            item.activeUntil ? <ChangeModal item={item} closeModal={closeModal} /> : null,
          ),
      },
      {
        content: (
          <Stack direction="ROW" align="center" size="2">
            <Icon size="16px" as={ReturnIcon} />
            <span>{t('Licenses:options.cancel')}</span>
          </Stack>
        ),
        handler: () =>
          showModal((closeModal) => <ConfirmCancelModal item={item} closeModal={closeModal} />),
      },
    ],
    [item],
  );

  return (
    <ActionButton
      appearance={EButtonAppearance.tertiary}
      size="XS"
      overlayPlacementX="right"
      options={options}
      square
    >
      <S.MoreHorizIcon />
    </ActionButton>
  );
});

const CHANNELS_WITHOUT_SUBJECTS_LIST = [
  EChannelType.WHATSAPP,
  EChannelType.MAX_BOT,
  EChannelType.MAX,
  EChannelType.TELEGRAM,
];

const Item = React.memo<{
  item: TLicense | TSubjectLicense;
  tenantId?: TItemId;
  hasTariffs: boolean;
  tariffsBySubject?: Record<number, TAvailableTariffResponse>;
  hasWhatsAppMauInfo?: boolean;
}>(({item, tenantId, hasWhatsAppMauInfo, tariffsBySubject, hasTariffs}) => {
  const {t} = useTranslation();
  const clientCardPath = useMatch({path: PATHS.COMPANY_CLIENT_CARD, end: false});
  const isClientPage = !!clientCardPath;
  const [isSubjectsOpen, setIsSubjectsOpen] = React.useState(false);
  const isWhatsApp = item.channelType === EChannelType.WHATSAPP;
  const isChannelWithoutSubjectsList = CHANNELS_WITHOUT_SUBJECTS_LIST.includes(item.channelType);

  const toggleSubjects = React.useCallback(() => setIsSubjectsOpen((prev) => !prev), []);

  const approvedSubjects = useGetApprovedSubjectsQuery(tenantId);

  const handleBlock = React.useCallback(() => {
    showModal((closeModal) => <CancelLicenseModal license={item} closeModal={closeModal} />);
  }, [item]);

  const subjects = React.useMemo(() => {
    if (isChannelWithoutSubjectsList) {
      if (!('subjectId' in item)) {
        return [];
      }

      const found = approvedSubjects.find((subject) => subject.id === item.subjectId);

      return found ? [found] : [];
    }

    return approvedSubjects.filter((subject) => subject.type === item.channelType);
  }, [isChannelWithoutSubjectsList, approvedSubjects]);

  const whatsAppMauInfo = React.useMemo(() => {
    if (!isWhatsApp) {
      return (
        <>
          <S.Td>-</S.Td>
          <S.Td>-</S.Td>
        </>
      );
    }

    const whatsAppItem = item as TWhatsAppLicense;

    return (
      <>
        <S.Td>
          {whatsAppItem.tariffMauCount
            ? t('Licenses:whatsAppMauInfo', {
                tariffCount: whatsAppItem.tariffMauCount,
                count: whatsAppItem.remainingMauCount,
              })
            : '-'}
        </S.Td>
        <S.Td>{whatsAppItem.amauCount ?? '-'}</S.Td>
      </>
    );
  }, [isWhatsApp, item]);

  const hasCollapse = !!subjects.length && !isChannelWithoutSubjectsList;

  const licenseSubjectId = 'subjectId' in item ? Number(item.subjectId) : null;
  const licenseTariff = licenseSubjectId ? tariffsBySubject?.[licenseSubjectId] : null;

  const handleOpenLicenseTariff = () => {
    if (!licenseTariff || !licenseSubjectId) {
      return;
    }

    showTariffModal({tariff: licenseTariff, subject: item as unknown as TSubject});
  };

  return (
    <S.Tr isCenter={false} isColumn>
      <S.Tr borderless>
        <S.Td>
          <S.Type onClick={toggleSubjects}>
            <Icon as={channelIconHash[item.channelType]} size="24px" />
            <NameLink title={item.name}>{item.name}</NameLink>
            {hasCollapse && <S.ArrowIcon as={KeyboardArrowDownIcon} open={isSubjectsOpen} />}
          </S.Type>
        </S.Td>
        <S.Td>{!hasCollapse && subjects[0]?.subject}</S.Td>
        <S.Td>
          <Stack direction="ROW" size="2" align="center">
            <Badge color={colorHash[item.status]}>
              {t(`Licenses:licenseStatus.${item.status}`)}
            </Badge>
            <PermissionGuard permissions={EPermission.PERMISSION_TENANT_LICENSE_BLOCK}>
              <>
                {isActiveLicense(item.status) &&
                  !isClientPage &&
                  !item.scheduledCancelAt &&
                  LICENSE_CHANNELS.includes(item.channelType) && (
                    <Button square size="XS" onClick={handleBlock}>
                      <Icon as={BlockIcon} size="12px" />
                    </Button>
                  )}
                {item.scheduledCancelAt && <Actions item={item} />}
              </>
            </PermissionGuard>
          </Stack>
        </S.Td>
        <LicenseDate item={item} />
        {hasTariffs && (
          <S.Td>
            {!!licenseTariff && (
              <InlineLinkButton onClick={handleOpenLicenseTariff}>
                {licenseTariff.active.name}
              </InlineLinkButton>
            )}
          </S.Td>
        )}
        {hasWhatsAppMauInfo && whatsAppMauInfo}
      </S.Tr>

      {hasCollapse && (
        <Collapse open={isSubjectsOpen} toggleOpen={() => {}} labelComponent={() => null}>
          {subjects.map((subject) => {
            const tariff = tariffsBySubject?.[subject.id];

            return (
              <S.Tr borderless key={subject.id}>
                <S.Td>
                  <S.CollapseContent>
                    <S.Type>
                      <Icon as={channelIconHash[item.channelType]} size="24px" />
                      <Text size="XS" weight={500}>
                        {item.name}
                      </Text>
                    </S.Type>
                  </S.CollapseContent>
                </S.Td>
                <S.Td>{subject.subject}</S.Td>
                <S.Td>
                  <Badge color={colorHash[item.status]}>
                    {t(`Licenses:licenseStatus.${item.status}`)}
                  </Badge>
                </S.Td>
                <S.Td />
                <S.Td>
                  {!!tariff && (
                    <InlineLinkButton onClick={() => showTariffModal({tariff, subject})}>
                      {tariff.active.name}
                    </InlineLinkButton>
                  )}
                </S.Td>
                <S.Td />
              </S.Tr>
            );
          })}
        </Collapse>
      )}
    </S.Tr>
  );
});

Item.displayName = 'Item';

export default Item;
